import {
    Logger,
    Utils,
    StringUtils,
    ObjectUtils,
    UriHelper,
    BrowserUtils,
} from 'neo-common';
import {AppBase} from '../AppBase';
import {GameDefinitionBase} from '../GameDefinitionBase';
import {GameConfigurationBase} from '../GameConfigurationBase';
import {GameQualities, GameQualitySize} from '../Types';
import {GameClientHostConsumerBase} from './GameClientHostConsumerBase';
import {GameClientOptions} from '../../Communication/Game/GameClientOptions';
import {GameClient} from '../../Communication/Game/GameClient';
import {IClientHost} from '../../ClientHost/IClientHost';
import {IRealityCheck} from '../../RealityCheck/IRealityCheck';
import {RealityCheckFactory} from '../../RealityCheck/RealityCheckFactory';
import {ClientHostDialogRequest} from '../../ClientHost/Types';

declare var require: any;

const defaultQualities: GameQualities = {
    'FHD': {
        width: 1920,
        height: 1080
    },
    'HD': {
        width: 1280,
        height: 720
    },
    'QHD': {
        width: 960,
        height: 540
    }
};

const appTypeName = 'Game';

/**
 * Base game application construction
 */
export abstract class GameAppBase<TGameConfiguration extends GameConfigurationBase,
    TGameDefinition extends GameDefinitionBase,
    TClientHostConsumer extends GameClientHostConsumerBase>
    extends AppBase<TGameConfiguration, TGameDefinition, TClientHostConsumer> {

    private _qualities: GameQualities;

    protected _gameClient: GameClient;
    protected _currentBrowser: string;
    protected _realityCheck: IRealityCheck;

    constructor() {
        super();

        this._qualities = this.createGameQualities();
        this._gameClient = null;
        this._currentBrowser = BrowserUtils.getNameAndVersion();
        this._realityCheck = null;

        this.handleRealityCheckTimerExpired = this.handleRealityCheckTimerExpired.bind(this);

        if (DEBUG) {
            Logger.debugGroup(this.loggerGroup, `Detected browser: `, this._currentBrowser);
        }
    }

    protected get supportsRealityCheck(): boolean {
        return true;
    }

    protected get appTypeName(): string {
        return appTypeName;
    }

    /**
     * Creates default game qualities list.
     * @returns {GameQualities} JSON object with defined qualities (key - quality name, value - size)
     */
    protected createGameQualities(): GameQualities {
        return ObjectUtils.clone(defaultQualities);
    }

    protected loadGameConfiguration(): Promise<any> {
        const self = this;
        let loggerGroup = `${this.loggerGroup}/loadGameConfiguration`;

        return super.loadGameConfiguration()
            .then(() => {
                if (!Utils.isNullOrUndefined(self._clientHost) && !Utils.isNullOrUndefined(self._clientHost.supports)) {
                    const serverConfigurationRequestAllowed = Utils.valueOrDefault(self._clientHost.supports.serverConfigurationRequestAllowed, true);
                    if (!serverConfigurationRequestAllowed) {
                        if (DEBUG) {
                            Logger.debugGroup(loggerGroup, `Loading game configuration from server was disallowed by client host '${self._clientHost.type}'.`);
                        }
                        return;
                    }
                }

                if (DEBUG) {
                    Logger.debugGroup(loggerGroup, `Loading game configuration from server...`);
                }

                let options = GameClientOptions.get();
                options.disableKeepAlive();
                let configGameClient = self.createGameClient(options);
                let version = this._preloader ? this._preloader.versionId : '';
                return configGameClient.sendConfigurationRequest(version)
                    .then(configResponse => {
                        let hasConfigResponse = !ObjectUtils.isNullOrEmpty(configResponse);
                        if (DEBUG) {
                            Logger.debugGroup(loggerGroup,
                                `Loading game configuration from server, received config from server? ` + (hasConfigResponse ? 'YES' : 'NO'));
                        }

                        if (hasConfigResponse && self._gameConfiguration) {
                            if (DEBUG) {
                                Logger.debugGroup(loggerGroup, 'Game configuration before merge from server: ',
                                    self._gameConfiguration.getRaw());
                            }

                            // merge current config with 'currency' specific confing (if any)
                            self._gameConfiguration.merge(configResponse, 'SERVER URL');

                            if (DEBUG) {
                                Logger.debugGroup(loggerGroup, 'Final game configuration: ',
                                    self._gameConfiguration.getRaw());
                            }
                        } else {
                            //server configuration is now required, if no response, set configuration to null (except packets mode)
                            if(!UriHelper.getPacketsMode()) {
                                self._gameConfiguration = null;
                                throw new Error(`Failed to request server configuration`);
                            }
                        }
                    })
                    .catch(err => {
                        if (err) {
                            Logger.warnGroup(loggerGroup, `Failed to request server configuration`);
                        }

                        throw err;
                    });
            });
    }

    /**
     * Gets default options for game client.
     * @returns {GameClientOptions} default options where keep alive is enabled and timer is set to '8 minutes'.
     */
    protected getGameClientOptions(): GameClientOptions {
        let options = GameClientOptions.get();

        if (this._appContext != null && this._appContext.gameConfiguration) {
            options.enabledKeepAlive(this._appContext.gameConfiguration.KeepAliveInterval);
            options.setKeepAliveRefreshTime(this._appContext.gameConfiguration.KeepAliveRefreshInterval);
            options.setServerRequestTimeout(this._appContext.gameConfiguration.ServerRequestTimeout);
        }

        return options;
    }

    /**
     * Gets new instance of game client used by game application.
     * @returns <GameClientBase> new instance of game client @GameClient.
     */
    protected initGameClient(): void {
        const self = this;
        if (this._gameClient === null) {
            let gameClient: GameClient = null;

            if (DEBUG) {
                var packetMode = UriHelper.getPacketsMode();
                if (packetMode) {
                    let gameKey = UriHelper.getGameKey();
                    let PacketGameClient = require('../../Tools/PacketClient/PacketClient').PacketGameClient;
                    gameClient = new PacketGameClient(gameKey);
                }
            }

            if (gameClient === null) {
                gameClient = this.createGameClient(this.getGameClientOptions());
            }

            const forceToolExternalSetup = window['ForceToolExternalSetup'] as any;
            if (!Utils.isNullOrUndefined(forceToolExternalSetup) && !Utils.isNullOrUndefined(forceToolExternalSetup.initialize) &&
                typeof (forceToolExternalSetup.initialize) === 'function') {

                const fns = {
                    isForceToolEnabled: () => {
                        return self._appContext.gameConfiguration.ForceToolEnabled;
                    },
                    isForceToolDebugEnabled: () => {
                        return self._appContext.gameConfiguration.ForceToolDebugEnabled;
                    },
                    forceToolDemoPINHash: () => {
                        return self._appContext.gameConfiguration.ForceToolDemoPINHash;
                    },
                    isGameCollectable: () => {
                        // TODO replace with real value!
                        return false;
                    }
                };

                forceToolExternalSetup.initialize(fns);
            }

            this._gameClient = gameClient;

            if (DEBUG) {
                if (Utils.isNullOrUndefined(this._gameClient)) {
                    Logger.errorGroup(this.loggerGroup, `Game client was NOT initialized.`);
                } else {
                    Logger.debugGroup(this.loggerGroup, `Game client was initialized.`);
                }
            }
        }
    }

    /**
     * @method pauseAnimations - pauses game animations base on requestAnimationFrame
     */
    protected abstract pauseAnimations(): void;

    /**
     * @method resumeAnimations - resumes game animations base on requestAnimationFrame
     */
    protected abstract resumeAnimations(): void;

    /**
     * Called within @exit() method, before @endGame() on IClientHost is made.
     * @returns {Promise<void>}
     * @remarks @endGame() on client host is called event when promise of @beforeExit() fails!
     */
    protected beforeExit(): Promise<any> {
        return Promise.resolve();
    }

    /**
     * Creates game client with specified @options.
     * @param options to be used to create new game client instance.
     * @returns game client instance which extends base class @GameClientBase.
     */
    protected abstract createGameClient(options: GameClientOptions): GameClient;

    /**
     * Creates game stage instance.
     * @param quality - quality key name
     * @param size - size of quality
     */
    protected abstract createStage(quality: string, size: GameQualitySize): void;

    /**
     * Gets fallback quality key
     * @returns {string} key of quality used as fallback.
     */
    protected get fallbackQuality(): string {
        return 'HD';
    }

    protected loadVersion(): Promise<any> {
        this.initStage();
        return super.loadVersion();
    }

    protected loadResources(): Promise<any> {
        this.applyWorkarounds();
        return super.loadResources();
    }

    private applyWorkarounds(): void {
        let workaroundId = 'preloader_audio';

        if (this._gameConfiguration != null) {
            let workaround_definition = this._gameConfiguration.getWorkaround(workaroundId);
            if (!Utils.isNullOrUndefined(workaround_definition)) {
                if (DEBUG) {
                    Logger.debugGroup(this.loggerGroup, `Found workaround '${workaroundId}' definition: `, workaround_definition);
                }

                let workaroundActive = BrowserUtils.checkExact(workaround_definition);
                if (workaroundActive) {
                    if (DEBUG) {
                        Logger.debugGroup(this.loggerGroup, `Workaround '${workaroundId}' will be applied, definition match current browser: `,
                            this._currentBrowser);
                    }

                    this._preloader.skipAudioResources = true;

                    if (DEBUG) {
                        Logger.debugGroup(this.loggerGroup, `Workaround '${workaroundId}' -> skip loading audio resources was set!`);
                    }
                } else {
                    if (DEBUG) {
                        Logger.debugGroup(this.loggerGroup, `Workaround '${workaroundId}' will not be applied, definition does not match current browser: `,
                            this._currentBrowser);
                    }
                }
            }
        }
    }

    protected initStage(): void {
        let qualityKey = window['QUALITY'] || '';
        let qualitySize = StringUtils.isNullOrEmpty(qualityKey) ? null : this._qualities[qualityKey];
        if (Utils.isNullOrUndefined(qualitySize)) {
            if (DEBUG) {
                Logger.errorGroup(this.loggerGroup,
                    `Could not find quality settings for '${qualityKey}',` +
                    ` using fallback quality '${this.fallbackQuality}'.`);

            }

            qualityKey = this.fallbackQuality;
            qualitySize = this._qualities[qualityKey];
        }

        if (qualitySize.width <= 0 || qualitySize.height <= 0) {
            qualitySize = this._qualities[qualityKey];
        }

        if (DEBUG) {
            Logger.debugGroup(this.loggerGroup,
                `Initializing stage for quality '${qualityKey}', width: ${qualitySize.width} and height: ${qualitySize.height}`);
        }

        this.createStage(qualityKey, qualitySize);
    }

    /**
     * Prepare load with loading packets mode (if enabled and DEBUG mode is active)
     * @returns {Promise<boolean>} true - if packets mode was not loaded and normal game mode can be loaded,
     * or false - means that packets mode was successfully loaded and normal game mode CANNOT be loaded.
     */
    protected prepareLoad(): Promise<boolean> {
        return Promise.resolve()
            .then(() => {
                let uriQuery = Utils.getURIQuery();
                let gameKey = UriHelper.getGameKey();
                let prepared = true;

                if (DEBUG) {
                    let packetsMode = UriHelper.getPacketsMode(uriQuery);
                    let packetsSetName = UriHelper.getPacketSetName(uriQuery);
                    prepared = !packetsMode;

                    if (packetsMode) {
                        let OfflinePackets = require('../../Tools/PacketClient/OfflinePackets').OfflinePackets;
                        let PacketPlayer = require('../../Tools/PacketClient/PacketPlayer').PacketPlayer;

                        // if packet set name is in query
                        if (!Utils.isNullOrUndefined(packetsSetName)) {
                            return OfflinePackets.hasLoadedSet(packetsSetName, gameKey)
                                .then((hasLoadedSet: boolean) => {
                                    return hasLoadedSet;
                                });
                        }

                        new PacketPlayer(gameKey);
                    }
                }

                return Promise.resolve(prepared);
            });
    }

    protected loadClientHost(): Promise<IClientHost> {
        var self = this;

        return super.loadClientHost()
            .then(clientHost => {
                if (this._clientHostConsumer) {
                    (this._clientHostConsumer as any)._exitGameFunc = function () {
                        return self.exit();
                    };
                }

                return clientHost;
            });
    }

    protected loadCompleted(): void {
        this.initGameClient();
        super.loadCompleted();
    }

    /**
     * Exist current game against client host
     */
    public exit(): Promise<any> {
        const self = this;

        if (DEBUG) {
            Logger.debugGroup(this.loggerGroup, `Calling exit()...`);
        }

        return this.beforeExit()
            .finally(() => self._clientHost.endGame(self, {reason: ''}));
    }

    /**
     * Look for history url in game configuration and if found do redirect to it.
     * @returns {null}
     */
    public history(): Promise<any> {
        if (DEBUG) {
            Logger.debugGroup(this.loggerGroup, `Calling history()...`);
        }

        this._clientHost.history(this);
        return Promise.resolve();
    }

    /**
     * Continues execution of paused game (after some dialog, etc).
     */
    public continue(): Promise<any> {
        const self = this;

        if (DEBUG) {
            Logger.debugGroup(this.loggerGroup, `Calling continue()...`);
        }

        return Promise.resolve()
            .then(() => {
                if (self._gameDefinition.GameClientCallsRealityCheckUrls) {
                    if (DEBUG) {
                        Logger.debugGroup(self.loggerGroup, `continue() -> calling 'reset reality check player url' ...`);
                    }

                    return self._gameClient.resetRealityCheck();
                }
            })
            .then(() => {
                self.resetRealityCheck();
            })
            .then(() => {
                return self._clientHost.continue(self);
            });
    }

    public getRealityCheckInfo(): Promise<any> {
        const self = this;
        if (DEBUG) {
            Logger.debugGroup(this.loggerGroup, `Calling continue()...`);
        }

        return Promise.resolve()
            .then(() => {
                return self._gameClient.getRealityCheckInfo();
            });
    }

    public closeRealityCheck(): Promise<any> {
        const self = this;

        return Promise.resolve()
            .then(() => {
                if (self._gameDefinition.GameClientCallsRealityCheckUrls) {
                    if (DEBUG) {
                        Logger.debugGroup(self.loggerGroup, `continue() -> calling 'reset reality check player url' ...`);
                    }

                    return self._gameClient.resetRealityCheck();
                }
            })
            .then(() => {
                self.resetRealityCheck();
            })
    }


    private resetRealityCheck(): void {
        if (this._realityCheck) {
            if (DEBUG) {
                Logger.debugGroup(this.loggerGroup, `reality check - calling reset.`);
            }

            return this._realityCheck.reset();
        }
    }

    protected startRealityCheck(): void {
        if (this.loadingSucceed) {
            let realityCheckInterval = this.gameConfiguration.RealityCheckInterval;
            let realityCheckStartInterval = this.gameConfiguration.RealityCheckStartInterval;
            let enableRealityCheck = realityCheckInterval > -1 && realityCheckStartInterval > -1;

            if (DEBUG) {
                let realityCheckInfo = `(realityCheckInterval: ${realityCheckInterval} sec, realityCheckStartInterval: ${realityCheckStartInterval} sec)`;

                if (enableRealityCheck) {
                    Logger.debugGroup(this.loggerGroup,
                        `startRealityCheck() -> detected and enabled 'Reality Check' from game configuration ` + realityCheckInfo);
                } else {
                    Logger.debugGroup(this.loggerGroup,
                        `startRealityCheck() -> 'Reality Check' was not detected thus not enabled ` + realityCheckInfo);
                }
            }

            if (enableRealityCheck) {
                const realityCheckType = this._gameDefinition.RealityCheckType;

                if (DEBUG) {
                    Logger.debugGroup(`${this.loggerGroup}`,
                        `startRealityCheck() -> Defined reality check type '${realityCheckType}' ...`);
                }

                this._realityCheck = RealityCheckFactory.create(realityCheckType);

                if (DEBUG) {
                    if (Utils.isNullOrUndefined(this._realityCheck)) {
                        Logger.debugGroup(this.loggerGroup,
                            `startRealityCheck() -> Could not create implementation of reality check, ` +
                            `RealityCheckFactory does not create valid reality check (Type: '${realityCheckType}') !`);
                    } else {
                        Logger.debugGroup(this.loggerGroup,
                            `startRealityCheck() -> Created reality check '${this._realityCheck.name}' v'${this._realityCheck.version}' (Type: '${realityCheckType}')`);
                    }
                }

                if (this._realityCheck) {
                    this._realityCheck.initialize(this, this.handleRealityCheckTimerExpired);
                    this._realityCheck.start();
                }
            }
        } else {
            if (DEBUG) {
                Logger.debugGroup(this.loggerGroup,
                    `startRealityCheck() -> Coult not create/start reality check because app was not successfully loaded (app.loadingSucceed -> ${this.loadingSucceed})`);
            }
        }
    }

    private handleRealityCheckTimerExpired(request: ClientHostDialogRequest): void {
        this._clientHost.showDialog(request);
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/GameEngine/Game/GameAppBase.ts
 **/